/**
 * Renders assistant text through the incremental markdown layer.
 *
 * Sealed blocks are memoised on their content key and never re-render while
 * text arrives below them. Only the open block updates. Sealed code blocks
 * are highlighted lazily, in an idle callback, from a shiki chunk that is
 * dynamically imported so it never touches the initial bundle. Everything
 * passes through the sanitiser before it reaches the DOM, highlighted or not.
 */

import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type RefObject,
} from 'react';

import { createIncrementalRenderer, type SealedBlock } from '@harness/stream-core/markdown';

import { sanitizeHtml } from '../lib/sanitize.ts';

const FENCE_INFO = /^(?:```|~~~)(\w+)?/;

type Idle = (callback: () => void) => number;

const requestIdle: Idle =
  'requestIdleCallback' in window
    ? (callback) =>
        window.requestIdleCallback(() => {
          callback();
        })
    : (callback) => window.setTimeout(callback, 200);

async function highlight(block: SealedBlock, dark: boolean): Promise<string | null> {
  const lang = FENCE_INFO.exec(block.source.trimStart())?.[1] ?? '';
  if (lang === '') return null;
  const body = block.source.split('\n').slice(1, -1).join('\n');
  try {
    // The dynamic import is the code splitting boundary: shiki and its
    // grammars never touch the initial chunk.
    const { highlightCode } = await import('../lib/highlight.ts');
    return await highlightCode(body, lang, dark);
  } catch {
    // An unknown language or a failed chunk load leaves the plain block in
    // place, which is already correct and already sanitised.
    return null;
  }
}

/**
 * A `<pre>` that overflows horizontally is a scrollable region, and one with
 * no focusable content inside it is unreachable by keyboard, which fails
 * WCAG 2.1.1. Markdown output is raw HTML mounted through
 * `dangerouslySetInnerHTML`, so there is no React element to hand a prop to;
 * this walks the mounted DOM once per render and makes every `pre` a
 * keyboard stop directly, the same fix `Inspector`'s frame list uses.
 */
function makeCodeBlocksFocusable(container: HTMLDivElement | null): void {
  if (container === null) return;
  for (const pre of container.querySelectorAll('pre')) {
    if (!pre.hasAttribute('tabindex')) pre.setAttribute('tabindex', '0');
  }
}

function useFocusableCodeBlocks(): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    makeCodeBlocksFocusable(ref.current);
  });
  return ref;
}

const Sealed = memo(
  function Sealed({ block }: { block: SealedBlock }): ReactElement {
    const plain = useMemo(() => sanitizeHtml(block.html), [block.html]);
    const [html, setHtml] = useState(plain);
    const [copied, setCopied] = useState(false);
    const askedRef = useRef(false);
    const containerRef = useFocusableCodeBlocks();

    useEffect(() => {
      if (!block.isCode || askedRef.current) return;
      askedRef.current = true;
      requestIdle(() => {
        const dark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        void highlight(block, dark).then((highlighted) => {
          if (highlighted !== null) setHtml(sanitizeHtml(highlighted));
        });
      });
    }, [block]);

    const copyCode = (): void => {
      const body = block.source.split('\n').slice(1, -1).join('\n');
      void navigator.clipboard.writeText(body).then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      });
    };

    if (block.isCode) {
      return (
        <div className="prose__code-wrapper">
          <button
            type="button"
            className="button button--quiet prose__code-copy"
            onClick={copyCode}
            aria-label={copied ? 'Code copied' : 'Copy code to clipboard'}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
          <div
            ref={containerRef}
            className="prose__block"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      );
    }

    return (
      <div ref={containerRef} className="prose__block" dangerouslySetInnerHTML={{ __html: html }} />
    );
  },
  (a, b) => a.block.key === b.block.key,
);

export function AssistantBody({
  text,
  streaming,
}: {
  text: string;
  streaming: boolean;
}): ReactElement {
  // One renderer per message instance, so the seal cache lives exactly as
  // long as the text it describes.
  const rendererRef = useRef<ReturnType<typeof createIncrementalRenderer> | null>(null);
  rendererRef.current ??= createIncrementalRenderer();
  const result = rendererRef.current.render(text);

  const openHtml = result.open === null ? null : sanitizeHtml(result.open.html);
  const openRef = useFocusableCodeBlocks();

  return (
    <div className="prose">
      {result.sealed.map((block) => (
        <Sealed key={block.key} block={block} />
      ))}
      {openHtml !== null && (
        <div
          ref={openRef}
          className="prose__block"
          dangerouslySetInnerHTML={{ __html: openHtml }}
        />
      )}
      {streaming && <span className="prose__cursor" aria-hidden="true" />}
    </div>
  );
}
